/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { Button } from '@msdyn365-commerce-modules/utilities';
import MsDyn365 from '@msdyn365-commerce/core';
import classname from 'classnames';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { fireAnalyticsLink } from '../../Utilities/analytics/clickTrack';
import { htmltoText } from '../../Utilities/analytics/htmlToText';
import { firePromotionClick, firePromotionImpression } from '../../Utilities/analytics/promotionClickTrack';
import { getDynamicImageSettings, getResponsiveImage } from '../../Utilities/get-dynamic-image';
import _getViewPort from '../../Utilities/get-view-port';
import { IMfrmHeroSlideViewProps } from './mfrm-hero-slide';
import { getAnalyticsConfig, getPromoAnalyticsConfig } from './tealium-analytics';

// tslint:disable: max-func-body-length
// tslint:disable: cyclomatic-complexity
export default (props: IMfrmHeroSlideViewProps) => {
  const {
    eyebrow,
    subCopy,
    ctaCopy,
    link,
    image,
    imageAriaLabel,
    seoText,
    className,
    useDynamicImages,
    dynamicImages,
    enhancedEcommTracking,
    config,
    heading,
    heroLayout,
    heroColor,
    heroTextColor
  } = props;

  const pagePath = MsDyn365.isBrowser ? (window.location.href.split(location.host)[1].includes('?') ? window.location.href.split(location.host)[1].split('?')[0] : window.location.href.split(location.host)[1]) : '';
  const text = config.heading ? htmltoText(config.heading.toString()).trim() : '';

  const analyticsConfig = getAnalyticsConfig(text, imageAriaLabel, config.tealiumEvent);
  const promoAnalyticsConfig = getPromoAnalyticsConfig(dynamicImages?.desktopImage?.altText, imageAriaLabel, text, pagePath, config.tealiumPromotion);

  const isPromotion = enhancedEcommTracking === 'promotion';
  const isTwoColLayout = heroLayout === 'twoCol' || heroLayout === 'twoColAlt';

  const handleAnalytics = () => {
    if (MsDyn365.isBrowser) {
      fireAnalyticsLink({ ...analyticsConfig });
      if (isPromotion) {
        firePromotionClick({ ...promoAnalyticsConfig });
      }

      if (link) {
        window.location.href = link;
      }
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    // @ts-ignore
    if ('utag' in window) {
      event.preventDefault();
      handleAnalytics();
    }
  };

  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '25px'
  });

  React.useEffect(() => {
    if (MsDyn365.isBrowser && isPromotion && inView) {
      firePromotionImpression({ ...promoAnalyticsConfig });
    }
  }, [inView]);

  let currentImage = image;
  // Only use responsive Image if dynamic Images defined
  if (dynamicImages && useDynamicImages) {
    currentImage = getResponsiveImage({
      desktop: dynamicImages?.desktopImage,
      tablet: dynamicImages?.tabletImage,
      mobile: dynamicImages?.mobileImage,
      defaultImg: props.config.image,
      imageSettingViewports: getDynamicImageSettings({ moduleName: 'mfrm-hero-slide' })
    });
  }

  const dataCy = heroLayout === 'twoColAlt' ? 'Sleep-Distrupter-Hero' : '';

  const standardLayout = (
    <div>
      {currentImage && <div className='hero-slide__img'> {currentImage} </div>}
      {heading && <div className={classname('hero-slide__details-container', heroTextColor)}>
        {eyebrow && <div className='hero-slide__eyebrow'>{eyebrow}</div>}
        {heading && <div className='hero-slide__copy'>{heading}</div>}
        {subCopy && <div className='hero-slide__subCopy'>{subCopy}</div>}
        {ctaCopy && <Button className='hero-slide__cta'> {ctaCopy}</Button>}
      </div>}
      {seoText && <div className='hero-slide__seo-text'>{seoText}</div>}
    </div>
  );

  const twoColLayout = isTwoColLayout && (
    <div className={classname('d-flex', heroLayout === 'twoCol' ? 'hero-slide__two-col' : 'hero-slide__two-col-alt', heroColor)} data-cy={dataCy}>
      <div className={classname('d-flex flex-column justify-content-center',  heroLayout === 'twoCol' ? `hero-slide__two-col-content` : `hero-slide__two-col-alt-content`, heroTextColor)}>
        <div className={classname(heroLayout === 'twoCol' ? '' : "hero-slide__two-col-alt-container")}>
        {eyebrow && <div> {eyebrow} </div>}
        {heading && <div> <h1 className='hero-slide__two-col-h1'> {heading} </h1> </div>}
        {subCopy && <div> {subCopy} </div>}
        {ctaCopy && <div> <Button className='hero-slide__cta'> {ctaCopy} </Button> </div>}
        </div>
      </div>
      {currentImage && <div className='hero-slide__img'> {currentImage} </div>}
      {seoText && <div className='hero-slide__seo-text'>{seoText}</div>}
    </div>
  );

  return (
    <a
      ref={ref}
      title={imageAriaLabel}
      href={link}
      className={
        classname('hero-slide', className && className)
      }
      onClick={handleClick}
    >
      {!isTwoColLayout && standardLayout}
      {isTwoColLayout && twoColLayout}
    </a>
  );
};
